<script>
import EcButton from '../ui/EcButton.vue'
import { useMailV2Store } from '../../stores/mailV2'

function createDefaultMailV2Settings() {
  return {
    default_locale: 'fr',
    from_name: '',
    from_email: '',
    reply_to_name: '',
    reply_to_email: '',
    logo_url: '',
    logo_height: 40,
    brand_primary_color: '#0091ca',
    brand_secondary_color: '#EF8152',
    footer_text: '',
    footer_links: [],
    social_links: [],
    unsubscribe_url: '',
    custom_css: '',
  }
}

export default {
  name: 'MailV2Settings',
  components: { EcButton },
  props: {
    holderType: { type: String, required: true },
    holderId: { type: [String, Number], required: true },
  },
  data: function () {
    return {
      store: useMailV2Store(),
      form: createDefaultMailV2Settings(),
      saving: false,
    }
  },
  watch: {
    holderId: function () {
      this.loadSettings()
    },
  },
  mounted: function () {
    this.loadSettings()
  },
  methods: {
    async loadSettings() {
      try {
        var data = await this.store.getSettings(this.holderType, this.holderId)
        if (data) {
          this.form = Object.assign({}, createDefaultMailV2Settings(), data)
        }
      } catch (e) {
        // keep defaults
      }
    },
    async save() {
      this.saving = true
      try {
        await this.store.updateSettings(this.holderType, this.holderId, this.form)
        this.$ecNotify(this.$ecTranslate('MV2.SETTINGS_SAVED'), 'success')
      } catch (err) {
        var resp = err && err.response && err.response.data
        if (resp && resp.errors && typeof resp.errors === 'object' && !Array.isArray(resp.errors)) {
          var messages = Object.values(resp.errors).flat().join('\n')
          this.$ecNotify(resp.message || messages, 'error')
        } else {
          this.$ecNotify((resp && resp.message) || this.$ecTranslate('MV2.SAVE_ERROR'), 'error')
        }
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<template>
  <div class="ec-mv2-settings" v-loading="store.loading">
    <form @submit.prevent="save">
      <!-- Sender defaults -->
      <div class="ec-mv2-settings-section">
        <h3 class="ec-mv2-settings-heading">{{ $ecTranslate('MV2.SENDER_DEFAULTS') }}</h3>
        <div class="ec-mv2-settings-grid">
          <div class="ec-mv2-field">
            <label class="ec-mv2-label">{{ $ecTranslate('MV2.FROM_NAME') }}</label>
            <input v-model="form.from_name" type="text" class="ec-mv2-input" />
          </div>
          <div class="ec-mv2-field">
            <label class="ec-mv2-label">{{ $ecTranslate('MV2.FROM_EMAIL') }}</label>
            <input v-model="form.from_email" type="email" class="ec-mv2-input" />
          </div>
          <div class="ec-mv2-field">
            <label class="ec-mv2-label">{{ $ecTranslate('MV2.REPLY_TO_NAME') }}</label>
            <input v-model="form.reply_to_name" type="text" class="ec-mv2-input" />
          </div>
          <div class="ec-mv2-field">
            <label class="ec-mv2-label">{{ $ecTranslate('MV2.REPLY_TO_EMAIL') }}</label>
            <input v-model="form.reply_to_email" type="email" class="ec-mv2-input" />
          </div>
          <div class="ec-mv2-field">
            <label class="ec-mv2-label">{{ $ecTranslate('MV2.DEFAULT_LOCALE') }}</label>
            <select v-model="form.default_locale" class="ec-mv2-input">
              <option value="fr">Français</option>
              <option value="en">English</option>
              <option value="es">Español</option>
              <option value="pt">Português</option>
              <option value="ar">العربية</option>
            </select>
          </div>
        </div>
      </div>

      <!-- Branding -->
      <div class="ec-mv2-settings-section">
        <h3 class="ec-mv2-settings-heading">{{ $ecTranslate('MV2.BRANDING') }}</h3>
        <div class="ec-mv2-settings-grid">
          <div class="ec-mv2-field ec-mv2-field--full">
            <label class="ec-mv2-label">{{ $ecTranslate('MV2.LOGO_URL') }}</label>
            <input v-model="form.logo_url" type="text" class="ec-mv2-input" placeholder="https://..." />
          </div>
          <div class="ec-mv2-field">
            <label class="ec-mv2-label">{{ $ecTranslate('MV2.LOGO_HEIGHT') }}</label>
            <input v-model.number="form.logo_height" type="number" class="ec-mv2-input" />
          </div>
          <div class="ec-mv2-field">
            <label class="ec-mv2-label">{{ $ecTranslate('MV2.PRIMARY_COLOR') }}</label>
            <div class="ec-mv2-color-picker">
              <input type="color" v-model="form.brand_primary_color" class="ec-mv2-color-input" />
              <span class="ec-mv2-color-value">{{ form.brand_primary_color }}</span>
            </div>
          </div>
          <div class="ec-mv2-field">
            <label class="ec-mv2-label">{{ $ecTranslate('MV2.SECONDARY_COLOR') }}</label>
            <div class="ec-mv2-color-picker">
              <input type="color" v-model="form.brand_secondary_color" class="ec-mv2-color-input" />
              <span class="ec-mv2-color-value">{{ form.brand_secondary_color }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="ec-mv2-settings-section">
        <h3 class="ec-mv2-settings-heading">{{ $ecTranslate('MV2.FOOTER') }}</h3>
        <div class="ec-mv2-settings-grid">
          <div class="ec-mv2-field ec-mv2-field--full">
            <label class="ec-mv2-label">{{ $ecTranslate('MV2.FOOTER_TEXT') }}</label>
            <textarea v-model="form.footer_text" class="ec-mv2-input ec-mv2-textarea" rows="3" />
          </div>
          <div class="ec-mv2-field ec-mv2-field--full">
            <label class="ec-mv2-label">{{ $ecTranslate('MV2.UNSUBSCRIBE_URL') }}</label>
            <input v-model="form.unsubscribe_url" type="text" class="ec-mv2-input" placeholder="https://..." />
          </div>
        </div>
      </div>

      <!-- Submit -->
      <div class="ec-mv2-settings-footer">
        <EcButton type="submit" variant="primary" :loading="saving" :disabled="saving" icon="save">
          {{ $ecTranslate('MV2.SAVE') }}
        </EcButton>
      </div>
    </form>
  </div>
</template>

<style scoped>
.ec-mv2-settings {
  padding: 4px 0;
}
.ec-mv2-settings-section {
  margin-bottom: 24px;
}
.ec-mv2-settings-heading {
  font-size: 16px;
  font-weight: 600;
  margin: 0 0 12px;
  color: var(--ec-text-color, #333);
}
.ec-mv2-settings-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}
.ec-mv2-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.ec-mv2-field--full {
  grid-column: 1 / -1;
}
.ec-mv2-label {
  font-size: 12px;
  font-weight: 600;
  color: #555;
}
.ec-mv2-input {
  padding: 8px 12px;
  border: 1px solid var(--ec-border-color, #dee2e6);
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
}
.ec-mv2-textarea {
  resize: vertical;
  font-family: inherit;
}
.ec-mv2-color-picker {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ec-mv2-color-input {
  width: 40px;
  height: 34px;
  border: 2px solid #dee2e6;
  border-radius: 6px;
  cursor: pointer;
  padding: 2px;
}
.ec-mv2-color-value {
  font-size: 12px;
  color: #999;
}
.ec-mv2-settings-footer {
  padding-top: 16px;
  border-top: 1px solid var(--ec-border-color, #e9ecef);
}
.ec-mv2-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;
  border: 1px solid var(--ec-border-color, #dee2e6);
  background: #fff;
  transition: all 0.15s;
}
.ec-mv2-btn--primary {
  background: var(--ec-primary-color, #0091ca);
  color: #fff;
  border-color: var(--ec-primary-color, #0091ca);
}
.ec-mv2-btn--primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.ec-mv2-spin {
  animation: ec-spin 1s linear infinite;
}
@keyframes ec-spin {
  to { transform: rotate(360deg); }
}
</style>
